import { useEffect, useState } from 'react';

export { layout } from '@vitness/tokens';

/**
 * True while the viewport is at or below `px`.
 *
 * `matchMedia` rather than a ResizeObserver on purpose: it fires only when the
 * breakpoint is actually crossed, where an observer fires on every pixel of a
 * drag and re-renders whatever subscribed. Use this for layout decisions that
 * can't be expressed in CSS — a chart that needs a different *shape*, not just
 * different styling. Anything CSS can do belongs in a media query.
 *
 * Pass a tier from `layout` (re-exported above), never a bare number. Every
 * width this hook is called with also exists as a media query in app.css, and
 * the two have to agree — a literal in each place is two copies of one decision
 * with nothing to keep them in step. `layout` is that single copy.
 */
export function useNarrow(px: number): boolean {
  const query = `(max-width: ${px}px)`;
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent | MediaQueryList) => setNarrow(e.matches);
    onChange(mq);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);

  return narrow;
}
